const randomObjectives = [
  'Acquire (obtain or steal) an important item or items.',
  'Capture one or more of the opponents.',
  'Activate a magical device.',
  'Frame a character or tarnish their reputation.',
  'Drive the opponent into a corner or ambush point.',
  'Stop a magical ritual, legal ceremony, or time-sensitive spell.',
  'Hold the line — keep the enemy from reaching a specific area or group.',
  'Plant evidence or a tracking device on a target.',
  'Secure a specific location ahead of another group\'s arrival.',
  'Harass the opponent to deplete their resources or keep them occupied.',
  'Destroy a piece of architecture, a statue, a shrine, or a weapon.',
  'Investigate a situation to confirm or deny existing information.',
];

export default function AdditionalGuidance() {
  return (
    <>
      <p className="rule-note"><em>This section provides additional guidance for preparing and running a session of Daggerheart.</em></p>

      <h3 id="story-beats">STORY BEATS</h3>
      <p>In storytelling, a beat is a moment that changes the trajectory of the narrative — a shift in the world, a significant action or reaction, an emotional revelation, or an important decision. Take turns with the players, narrating a beat and then letting them react and carry the scene forward with their own beats. When preparing for a session, plan in terms of the moments that give shape to each scene or sequence, rather than pre-scripting specific details or exchanges.</p>

      <h3 id="preparing-combat">PREPARING COMBAT ENCOUNTERS</h3>
      <p>Build the hurdles the PCs face around the question of "What helps tell the story?" Enemies, environments, and hazards are the tools for heightening tension and creating drama. Ensure that combat is being used to give players more information about the unfolding story, revealing the world, the plot, or the characters.</p>

      <h3 id="battles-narrative">BATTLES AND NARRATIVE</h3>
      <p>Dynamic battles create suspense by forcing players to choose between their various objectives, engaging their character's motivations and weaknesses, and creating the crucible that the players use to forge their characters into legendary heroes. When preparing combat encounters:</p>
      <ul className="two-col-list">
        <li>Consider the narrative function of the battle</li>
        <li>Base adversaries' moves on their motives</li>
        <li>Use dynamic environments to bring the battleground to life</li>
        <li>Add enemies that can interact with the PCs' features and special abilities</li>
      </ul>

      <h3 id="session-rewards">SESSION REWARDS</h3>
      <p>Reward players at the end of a session with:</p>
      <ul>
        <li>Useful information</li>
        <li>Story hooks</li>
        <li>Loot</li>
        <li>Gold</li>
        <li>Access to new equipment or enhancements</li>
      </ul>

      <h3 id="crafting-scenes">CRAFTING SCENES</h3>
      <p>Whenever you start a session, arrive at a new place, or change the situation, tell the players what they need to know by thinking with all of your senses and sharing something unique or unexpected about the fiction.</p>

      <h3 id="engaging-players">ENGAGING YOUR PLAYERS</h3>
      <p>Keep your players engaged by:</p>
      <ul className="two-col-list">
        <li>Rotating the Focus between the PCs</li>
        <li>Tying Together Story Elements</li>
        <li>Engaging Quiet Players</li>
        <li>Using Visual Aids</li>
        <li>Encouraging Unguided Play</li>
        <li>Confronting the PCs with internal and external conflicts</li>
        <li>Raising the Stakes by Spending Fear</li>
        <li>Layering Goals Other than Attrition into Combat (see Table of Random Objectives below)</li>
      </ul>

      <h3 id="random-objectives">TABLE OF RANDOM OBJECTIVES</h3>
      <table className="equipment-table content-narrow" style={{ marginBottom: '16px' }}>
        <thead>
          <tr>
            <th className="objective-roll-col">1d12</th>
            <th>Objective</th>
          </tr>
        </thead>
        <tbody>
          {randomObjectives.map((obj, i) => (
            <tr key={i}>
              <td className="objective-roll-col" style={{ textAlign: 'center' }}><strong>{i + 1}</strong></td>
              <td style={{ fontSize: '13px' }}>{obj}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 id="phased-battles">PHASED BATTLES</h3>
      <p>Make battles dynamic by shifting the nature of their enemies or environment mid-combat:</p>
      <ul className="content-narrow">
        <li>Change the Terms of Engagement</li>
        <li>Alter the Environment</li>
        <li>Evolve the Opposition</li>
      </ul>

      <h3 id="using-downtime">USING DOWNTIME</h3>
      <p>Use downtime scenes as a pressure release valve to vary the intensity of the story and give the PCs room to breathe.</p>
      <p>Empower your players to frame their own downtime scenes. Ask the players what it looks like as they tend to their wounds or unwind together, encouraging them to take the reins and work with other players whose characters are involved.</p>

      <h3 id="projects-downtime">PROJECTS DURING DOWNTIME</h3>
      <p>The Work on a Project downtime move requires more GM input than other downtime moves and is best suited for long-term endeavors the PCs wish to undertake.</p>
      <p>These projects are typically tracked using a Progress Countdown. When deciding the starting value of the countdown, consider the complexity of the project, the availability of relevant tools, and the impact of the project on the story.</p>
      <p>Simple projects advance their countdown each time a player uses the Work on a Project move, but complex projects require a roll.</p>

      <h3 id="extended-downtime">EXTENDED DOWNTIME</h3>
      <p>When you fast-forward the story across an extended period, use montages to illustrate the passage of time. You gain 1d6 Fear per PC and advance any long-term countdowns as appropriate.</p>

      <h3 id="campaign-frames">CAMPAIGN FRAMES</h3>
      <p>A campaign frame provides inspiration, tools, and mechanics to support a particular type of story at the table.</p>
      <p>Every campaign frame has a complexity rating that indicates how much its mechanics deviate from or expand upon the Daggerheart core ruleset.</p>
      <p>Each campaign frame includes the following sections:</p>
      <ul className="two-col-list">
        <li>A <strong>pitch</strong> to present to players</li>
        <li>Suggestions and guidance on <strong>tone, feel, themes, and touchstones</strong></li>
        <li>An overview of the campaign's <strong>background</strong></li>
        <li>Guidance for fitting <strong>communities, ancestries, and classes</strong> into the setting</li>
        <li><strong>Principles</strong> for players and GMs to focus on during the campaign</li>
        <li>Unique setting <strong>distinctions</strong></li>
        <li>An <strong>inciting incident</strong> to launch the campaign</li>
        <li><strong>Special mechanics</strong> to use during the campaign</li>
        <li><strong>Questions to consider</strong> during session zero</li>
      </ul>
      <p className="rule-note"><em>You can find each campaign frame map in the appendix of the core rulebook or at daggerheart.com/downloads.</em></p>
    </>
  );
}
